import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ReviewList } from './ReviewList';
import type {
  ChoiceKey,
  ReviewEntry,
  StatusMessage,
  TestPayload,
} from './types';

type TestPanelProps = {
  test: TestPayload;
  answers: Record<string, ChoiceKey | undefined>;
  onSelectAnswer: (questionId: string, choice: ChoiceKey) => void;
  onSubmit: () => void | Promise<void>;
  status: StatusMessage | null;
  review: ReviewEntry[] | null;
};

export function TestPanel({
  test,
  answers,
  onSelectAnswer,
  onSubmit,
  status,
  review,
}: TestPanelProps) {
  const handleSubmit = () => {
    void onSubmit();
  };

  return (
    <div className="card card-info card-outline">
      <div className="card-header border-0 d-flex align-items-center justify-content-between">
        <h3 className="card-title mb-0">
          <i className="fas fa-pen-alt me-2" aria-hidden="true" /> Test
        </h3>
        <button type="button" className="btn btn-success btn-sm" onClick={handleSubmit}>
          <i className="fas fa-check me-1" aria-hidden="true" /> Submit answers
        </button>
      </div>
      <div className="card-body">
        {(test.questions || []).map((q, idx) => (
          <div key={q.id} className="mb-4 p-3 border rounded bg-light question-block">
            <div className="d-flex align-items-start">
              <span className="badge bg-info me-2">Q{idx + 1}</span>
              <div className="markdown flex-grow-1">
                <ReactMarkdown remarkPlugins={[remarkGfm as any]}>{q.prompt}</ReactMarkdown>
              </div>
            </div>
            <div className="mt-3">
              {(['A', 'B', 'C', 'D'] as ChoiceKey[]).map((choice) => (
                <div key={choice} className="form-check mb-2 choice-option">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={q.id}
                    id={`${q.id}-${choice}`}
                    value={choice}
                    checked={answers[q.id] === choice}
                    onChange={() => onSelectAnswer(q.id, choice)}
                  />
                  <label className="form-check-label" htmlFor={`${q.id}-${choice}`}>
                    <strong className="me-1">{choice}.</strong>
                    {q.choices[choice]}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}

        {status && status.text && (
          <div className={`alert alert-${status.type === 'err' ? 'danger' : status.type === 'ok' ? 'success' : 'info'}`} role="status">
            {status.text}
          </div>
        )}

        {review && (
          <div className="mt-4">
            <h4 className="h5 mb-3">Answer review</h4>
            <ReviewList entries={review} />
          </div>
        )}

        <div className="mt-4 d-flex justify-content-end">
          <button type="button" className="btn btn-success btn-sm" onClick={handleSubmit}>
            <i className="fas fa-check me-1" aria-hidden="true" /> Submit answers
          </button>
        </div>
      </div>
    </div>
  );
}
